import Vue from "vue";
import { mapGetters } from "vuex";
import _ from "lodash";
import moment from "moment";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Sidebar from "@/components/sidebar/Sidebar.vue";
import Datepicker from "@/components/datepicker/Datepicker.vue";
import Dropdown from "@/components/dropdown/Dropdown.vue";
import Loader from "@/components/loader/Loader.vue";
import Player from "@/components/player/Player.vue";
import Search from "@/components/search/Search.vue";
import CamsList from "@/components/camsList/CamsList.vue";
import { EventBus } from "@/services/EventBus";
import { getViewName } from "@/utils";

export default {
  name: "Alarms",
  components: {
    Datepicker,
    Sidebar,
    VuePerfectScrollbar,
    Dropdown,
    Loader,
    Player,
    Search,
    CamsList
  },
  data() {
    return {
      loading: false,
      groups: {
        views: {
          name: "global.views",
          placeholder: "placeHolders.findViews",
          search: "",
          open: true
        },
        cams: {
          name: "global.cameras",
          placeholder: "placeHolders.findCameras",
          search: "",
          open: true
        }
      },
      filters: {
        open: true
      },
      datepicker: {
        open: false,
        start: moment()
          .startOf("day")
          .format(),
        stop: moment()
          .endOf("day")
          .format()
      },
      searchAlarms: {
        placeholder: "placeHolders.findAlarms",
        value: ""
      },
      max_count: 10,
      prevRoute: null,
      viewsFilterHeight: 0,
      prevCameraMode: null
    };
  },
  created() {
    this.getAlarms();
    this.prevCameraMode = this.$store.state.mode;
  },
  mounted() {
    this.updateScroll();
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from;
    });
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit("setMode", this.prevCameraMode);
    next();
  },
  computed: {
    ...mapGetters({
      cams: "getCams",
      views: "getAlarmViews",
      activeViews: "getAlarmsActiveViews",
      list: "getAlarms"
    }),
    activeAlarm() {
      return (
        this.list.find(item => item.id === this.$route.params.alarmId) || null
      );
    },
    selectedCamsIds() {
      let queryCams = this.$route.query.cams
        ? this.$route.query.cams.split(",")
        : [];
      let queryView =
        (this.activeViews &&
          this.activeViews.length &&
          this.activeViews.reduce(
            (result, { cams }) => [...result, ...cams.map(cam => cam.id)],
            []
          )) ||
        [];

      return [...new Set([...queryCams, ...queryView])];
    },
    isItemSelected() {
      return (key, item) => {
        if (!key || !item || !item.id) return false;
        return (
          !!(
            this.$route.query[key] &&
            this.$route.query[key]
              .split(",")
              .find(id => id === item.id.toString())
          ) || false
        );
      };
    },
    alarms() {
      let camsIds = this.selectedCamsIds.length
        ? this.selectedCamsIds
        : this.cams.map(cam => cam.id);

      let result = this.list.filter(item => {
        return (
          camsIds.indexOf(item.camId) > -1 &&
          item.name
            .toLowerCase()
            .indexOf(this.searchAlarms.value.toLowerCase().trim()) !== -1
        );
      });

      result = _.groupBy(result, item =>
        moment
          .unix(item.timestamp)
          .startOf("day")
          .format()
      );

      return result;
    },
    filteredItems() {
      return (items, search) => {
        return (
          (items &&
            items.length &&
            items.filter(
              item => item.name.toLowerCase().indexOf(search.toLowerCase()) > -1
            )) ||
          []
        );
      };
    },
    isEmpty() {
      return _.isEmpty(this.alarms);
    },
    playerData() {
      return {
        mode: 1,
        layout: 1,
        camera_list: this.activeAlarm ? [this.activeAlarm.camId] : [],
        time: this.activeAlarm ? this.activeAlarm.time : null
      };
    },
    dateStart() {
      const { datepicker } = this;
      return (
        (datepicker && datepicker.start && this.getDate(datepicker.start)) ||
        null
      );
    },
    dateStop() {
      const { datepicker } = this;
      return (
        (datepicker && datepicker.stop && this.getDate(datepicker.stop)) || null
      );
    }
  },
  watch: {
    filteredCams() {
      this.updateScroll();
    },
    views() {
      this.updateScroll();
    },
    alarms() {
      this.updateScroll(this.$refs.alarmsScroll);
    },
    $route(to, from) {
      this.$nextTick(() => {
        EventBus.$emit("views:select");
      });
    }
  },
  methods: {
    getViewName,
    getDate(date) {
      return date
        ? moment(date)
            .locale(this.$store.state.i18n.locale)
            .format("DD MMMM YYYY")
            .toLowerCase()
        : null;
    },
    selectDate(date) {
      this.datepicker = {
        ...this.datepicker,
        start: date.start,
        stop: date.end,
        open: false
      };
      this.getAlarms();
    },
    toggleGroup(type) {
      this.groups[type].open = !this.groups[type].open;
      this.updateScroll();
    },
    updateScroll() {
      const viewsScroll = this.$refs.viewsScroll || null;
      const camsScroll = this.$refs.camsScroll || null;
      const update = ref => {
        if (ref)
          this.$nextTick(() => {
            ref.update();
            this.viewsFilterHeightCheck();
          });
      };
      if (viewsScroll) update(viewsScroll);
      if (camsScroll) update(camsScroll);
    },
    getComment(alarm) {
      if (
        alarm.action === "SPEAKER_STATE_READY" ||
        alarm.action === "SPEAKER_STATE_ACQUIRED"
      ) {
        return `${Vue.i18n.translate("eventStatus.EVENT_" + alarm.action)}\n${
          alarm.comment
        }`;
      }

      if (alarm.comment) return alarm.comment;

      if (alarm.description) return alarm.description;

      return Vue.i18n.translate("eventStatus.EVENT_" + alarm.action);
    },
    getAlarms() {
      this.loading = true;

      setTimeout(() => {
        this.$store
          .dispatch("getAlarms", {
            cams: this.cams,
            params: {
              start_time: this.datepicker.start,
              stop_time: this.datepicker.stop,
              max_count: 30
            }
          })
          .then(alarms => {
            this.updateScroll(this.$refs.alarmsScroll);
            this.loading = false;
          });
      }, 500);
    },
    toggleAlarm(alarm) {
      let query = {
        cams: this.$route.query.cams,
        view: this.$route.query.view
      };

      const route = {
        name: "alarms",
        params: {
          alarmId: this.$route.params.alarmId === alarm.id ? null : alarm.id
        },
        query
      };

      this.$store.commit(
        "setMode",
        route.params.alarmId ? 1 : this.prevCameraMode
      );

      this.$router.replace(route);
    },
    goBack() {
      const paramsRoute = Object.keys(this.$route.params).filter(
        key => this.$route.params[key] !== undefined
      );
      if (paramsRoute.length) {
        this.$router.push({ ...this.$route, params: {} });
      } else if (this.prevRoute) {
        this.$router.push(this.prevRoute);
      } else {
        this.$router.push("/");
      }
    },
    resetRouterQuery(key) {
      const query = { ...this.$route.query };
      if (query[key]) {
        delete query[key];
        this.$router.push({ query });
      }
    },
    addedRouterQuery(key, item) {
      if (!key || !item || !item.id) return;
      const itemQuery = this.$route.query[key];
      const items = itemQuery ? itemQuery.split(",") : [];
      const itemsFilter =
        (items &&
          items.length &&
          items
            .map(id => (id === item.id.toString() ? null : id))
            .filter(id => id)) ||
        [];
      const result = [
        ...(itemsFilter.length === items.length
          ? [...itemsFilter, item.id]
          : itemsFilter)
      ].join(",");
      const query = { ...this.$route.query, [key]: result };
      if (!result) delete query[key];
      this.$router.push({ query });
    },
    viewsFilterHeightCheck() {
      this.$nextTick(() => {
        const viewsBlock = this.$refs["views-filter-group"];
        this.viewsFilterHeight = (viewsBlock && viewsBlock.clientHeight) || 0;
      });
    }
  }
};



// WEBPACK FOOTER //
// ./src/modules/alarms/alarms.js